<template>
  <div id="aside">
    <SogFlexColumn style="align-items: flex-start">
      <SogCard>
        <SogCardActions v-show="!_loadDetails">
          <SogCardActionButton
            mdi-icon="mdi-information"
            tooltip="Details anzeigen"
            @click="onDetails"
          />
          <SogCardActionButton
            v-show="!_vehicleRegistration?.insuranceId"
            mdi-icon="mdi-shield-link-variant"
            tooltip="Verknüpfen"
            @click="onOpen('link')"
          />
          <SogCardActionButton
            v-show="_vehicleRegistration?.insuranceId"
            mdi-icon="mdi-shield"
            tooltip="Versicherungskarte"
            @click="onPolicy"
          />
          <SogCardActionButton
            v-show="_vehicleRegistration?.insuranceId"
            mdi-icon="mdi-shield-remove"
            tooltip="Verknüpfung löschen"
            @click="onOpen('unlink')"
          />
          <SogCardActionButton
            v-show="_vehicleRegistration?.ignore==='Y' || !_vehicleRegistration?.insuranceId"
            mdi-icon="mdi-close"
            tooltip="Ignorieren"
            @click="onOpen('ignore')"
          />
          <VehicleRegistrationLinkDialog
            ref="_dialogLink"
            target="#aside"
            :vehicle-registration-id="_vehicleRegistration?.id"
            @linked="load"
          />
          <SogDialog
            ref="_dialogUnlink"
            target="#aside"
            :dialog-buttons="SogDialogButtons.OkCancel"
            @click-ok="onOkUnLink"
            @click-cancel="onCancelLink('unlink')"
          >
            <SogTextBox
              v-model="_reason"
              width="100%"
              :multiline="true"
              placeholder="Grund fürs Löschen"
            />
          </SogDialog>
          <SogDialog
            ref="_dialogIgnore"
            target="#aside"
            :dialog-buttons="SogDialogButtons.OkCancel"
            @click-ok="onOkIgnore"
            @click-cancel="onCancelLink('ignore')"
          >
            <SogTextBox
              v-model="_reason"
              :multiline="true"
              width="100%"
              placeholder="Grund fürs Ignorieren"
            />
          </SogDialog>
        </SogCardActions>
        <SogCardContent v-show="!_loadDetails">
          <VehicleRegistrationComponent
            :data="_vehicleRegistration"
          />
        </SogCardContent>
        <SogCardContent>
          <SogSkeleton
            v-show="_loadDetails"
          />
        </SogCardContent>
      </SogCard>
      <SogCard v-show="_loadHistory || _history.length>0">
        <SogCardTitle>Historie</SogCardTitle>
        <SogCardContent v-show="!_loadHistory">
          <HistoryListComponent :data="_history" />
        </SogCardContent>
        <SogCardContent>
          <SogSkeleton v-show="_loadHistory" />
        </SogCardContent>
      </SogCard>
      <SogCard>
        <SogCardTitle>Gdv Nachrichten</SogCardTitle>
        <SogCardContent v-show="!_loadDetails">
          <SogFlexColumn>
            <GdvMessageIdComponent
              v-for="(data,i) in _gdvMessageIds"
              :key="i"
              :item="data"
            />
          </SogFlexColumn>
        </SogCardContent>
        <SogCardContent>
          <SogSkeleton v-show="_loadDetails" />
        </SogCardContent>
      </SogCard>
      <SogCard v-show="_memos">
        <SogCardTitle>Bemerkungen</SogCardTitle>
        <SogCardContent v-show="!_loadDetails">
          <SogFlexColumn>
            <MemoComponent
              :memo="_memos"
            />
          </SogFlexColumn>
        </SogCardContent>
        <SogCardContent>
          <SogSkeleton v-show="_loadDetails" />
        </SogCardContent>
      </SogCard>
      <SogCard v-show="_loadFiles || _files.length>0">
        <SogCardTitle>Anhänge</SogCardTitle>
        <SogCardContent v-show="_loadFiles">
          <SogSkeleton />
        </SogCardContent>
        <SogCardContent
          v-for="file in _files"
          v-show="!_loadFiles"
          :key="file.id"
        >
          <FileCardComponent :file="file">
            <SogCardActionButton
              mdi-icon="mdi-download"
              tooltip="Download"
              @click="onDownload(file)"
            />
          </FileCardComponent>
        </SogCardContent>
      </SogCard>
    </SogFlexColumn>
  </div>
</template>

<script setup lang="ts">

import {reactive, ref, watch} from "vue";
import {useNavigationStore} from "@/modules/Navigation/store";
import {storeToRefs} from "pinia";
import SogSkeleton from "@/shared/ui/SogSkeleton.vue";
import VehicleRegistration from "@/shared/model/VehicleRegistration";
import apiVehicleRegistration from "@/modules/VehicleRegistration/api";
import VehicleRegistrationComponent
  from "@/modules/VehicleRegistration/components/VehicleRegistrationFullComponent.vue";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import HistoryListComponent from "@/modules/History/components/HistoryListComponent.vue";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import apiHistory from "@/modules/History/api";
import History from "@/shared/model/History";
import GdvMessageIdComponent from "@/modules/VehicleRegistration/components/GdvMessageIdComponent.vue";
import SogFlexColumn from "@/shared/ui/SogFlexColumn.vue";
import getGuid from "@/shared/utils/getGuid";
import {SogDialogButtons} from "@/shared/ui/types/SogDialogButtons";
import SogDialog from "@/shared/ui/SogDialog.vue";
import notificationService from "@/shared/services/NotificationService";
import SogTextBox from "@/shared/ui/SogTextBox.vue";
import VehicleRegistrationLinkDialog
  from "@/modules/VehicleRegistration/components/VehicleRegistrationLinkDialog.vue";
import MemoComponent from "@/modules/VehicleRegistration/components/MemoComponent.vue";
import {IdType} from "@/shared/model/idType";
import apiFileUtils from "@/modules/FileUtils/api";
import ListFile from "@/shared/model/ListFile";
import FileCardComponent from "@/modules/FileUtils/components/FileCardComponent.vue";
import apiFile from "@/shared/services/apiFile";
import {useSpinner} from "@/shared/composables/SogSpinner";

const _navigationStore = useNavigationStore();
const {currentVehicleRegistrationId} = storeToRefs(_navigationStore);
const _vehicleRegistrationId = ref<string>("")
const _loadDetails = ref(true)
const _loadHistory = ref(true)
const _loadFiles = ref(true)
const _history: History[] = reactive<History[]>([])
const _gdvMessageIds = ref<string[]>([])
const _memos = ref<string>("")
const _key = ref<string>("")
const _reason = ref<string>("")
const _vehicleRegistration = ref<VehicleRegistration>(new VehicleRegistration())
const _dialogLink = ref<InstanceType<typeof VehicleRegistrationLinkDialog> | null>(null)
const _dialogIgnore = ref<InstanceType<typeof SogDialog> | null>(null)
const _dialogUnlink = ref<InstanceType<typeof SogDialog> | null>(null)
const _files = ref<ListFile[]>([])
const _spinner = useSpinner("aside")


watch(currentVehicleRegistrationId, async () => {
  if (!currentVehicleRegistrationId.value) {
    return
  }
  if (currentVehicleRegistrationId.value === _vehicleRegistrationId.value) {
    return;
  }

  _vehicleRegistrationId.value = currentVehicleRegistrationId.value
  await load()
})

async function load() {
  if (!currentVehicleRegistrationId) {
    return
  }
  _vehicleRegistrationId.value = currentVehicleRegistrationId.value ?? '';

  _key.value = getGuid();
  const t_loadDetails = loadDetails(_key.value);
  const t_loadHistory = loadHistory(_key.value);
  const t_loadFiles = loadFiles(_key.value);

  await Promise.allSettled([t_loadDetails, t_loadHistory,t_loadFiles])

}

async function loadDetails(key: string) {
  if (!_vehicleRegistrationId.value)
    return
  _loadDetails.value = true
  const res = await apiVehicleRegistration.load(_vehicleRegistrationId.value)
  if (_key.value !== key) {
    return
  }
  if (res) {
    _vehicleRegistration.value = res.vehicleRegistration
    _gdvMessageIds.value = res?.vehicleRegistration.gdvMessageIds?.split('|').filter(Boolean) ?? []
    _memos.value = res?.vehicleRegistration.memo ?? ""
  }
  _loadDetails.value = false
}

async function loadHistory(key: string) {
  if (!_vehicleRegistrationId.value)
    return
  _loadHistory.value = true
  _history.length = 0
  const lst = await apiHistory.listVehicleRegistration(_vehicleRegistrationId.value)
  if (_key.value !== key) {
    return
  }

  if (lst) {
    _history.push(...lst)
  }
  _loadHistory.value = false
}

async function loadFiles(key: string) {
  if (!_vehicleRegistrationId.value) {
    return;
  }
  try {
    _loadFiles.value = true
    const listFile = await apiFileUtils.listFileStore(_vehicleRegistrationId.value)
    if (_key.value !== key) {
      return
    }
    if (listFile) {
      _files.value = listFile?.sort((a, b) => (a.date && b.date) ? Date.parse(b.date.toString()) - Date.parse(a.date.toString()) : 0) ?? [];
    }
  } finally {
    _loadFiles.value = false
  }
}


async function onDetails() {
  await _navigationStore.showObject(IdType.VehicleRegistration, _vehicleRegistrationId.value);
}

function onOpen(action: string) {
  if (action === "link")
    _dialogLink.value?.show()
  else if (action === "unlink")
    _dialogUnlink.value?.show()
  else
    _dialogIgnore.value?.show()
}

function onCancelLink(action: string) {
  if (action === "unlink") {
    _dialogUnlink.value?.hide()
    _reason.value = ""
  } else {
    _dialogIgnore.value?.hide()
    _reason.value = ""
  }

}

async function onOkUnLink() {

  if (!_reason.value) {
    notificationService.error("Bitte einen Grund eingeben")
    return
  }
  const res = await apiVehicleRegistration.insuranceLink(_vehicleRegistration.value?.id ?? "", _vehicleRegistration.value?.id ?? "", _reason.value, true)
  if (res) {
    notificationService.success("Zulassung erfolgreich unverknüpft")
    _reason.value = ""
    _dialogUnlink.value?.hide()
    await load()

  }
}

async function onOkIgnore() {

  if (!_reason.value) {
    notificationService.error("Bitte einen Grund eingeben")
    return
  }
  const res = await apiVehicleRegistration.ignore(_vehicleRegistration.value?.id ?? "", _reason.value)
  if (res) {
    notificationService.success("Zulassung erfolgreich ignoriert")
    _reason.value = ""
    _dialogIgnore.value?.hide()
    await load()

  }

}


function onPolicy() {
  if (_vehicleRegistration.value.insuranceId) {
    _navigationStore.currentInsuranceId = _vehicleRegistration.value.insuranceId
  }
}

async function onDownload(file: ListFile) {
  if (!file.personId)
    return;
  try {
    _spinner.show()
    const fileId = await apiFileUtils.initDownloadFile(file.id ?? "");
    if (fileId)
      await apiFile.downloadTempFile(fileId)

  } finally {
    _spinner.hide()
  }
}

</script>

<style scoped>
</style>
